import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../lib/responsiveImageSizes';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import { readBlockCtaIntent, resolveBlockCtaHref } from '../../lib/resolveSectionHref';
import type { BlockRenderProps } from '../../types';

export function HeroQuietLuxury({
  block,
  locale,
  defaultLocale = locale,
  isActive,
  activeEditTarget,
  editable,
  headingLevel = 'h1',
  pages,
  siteMode,
  allBlocks,
}: BlockRenderProps) {
  const Heading = headingLevel;
  const eyebrow = pickBlockField(block.content, 'eyebrow', locale);
  const heading = pickBlockField(block.content, 'heading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryIntent = readBlockCtaIntent(block.content, locale, {
    hrefFieldKey: 'ctaPrimaryHref',
    fallback: 'book',
  });
  const ctaPrimaryHref = resolveBlockCtaHref(ctaPrimaryIntent, {
    locale,
    defaultLocale,
    content: block.content,
    hrefFieldKey: 'ctaPrimaryHref',
    siteMode,
    pages,
    allBlocks,
  });
  const heroImage = pickMediaUrl(block, 'heroImage');
  const heroImageSrc = heroImage ? buildSiteImageProxyUrl(heroImage, 1400, 78) : undefined;
  const heroImageSrcSet = heroImage
    ? buildSiteImageSrcSet(heroImage, [480, 768, 1200, 1600], 78)
    : undefined;
  const imageAlt = heading || eyebrow || 'Hero';
  const target = isActive ? activeEditTarget : null;

  return (
    <section
      className={['ws-hero-reveal', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="top"
    >
      <div className="ws-hero-reveal__visual" aria-hidden={!heroImage}>
        {heroImage ? (
          <EditableMedia
            fieldKey="heroImage"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-reveal__photo"
            src={heroImageSrc}
            srcSet={heroImageSrcSet}
            sizes={SITE_IMAGE_SIZES.heroReveal}
            alt={imageAlt}
            width={1200}
            height={900}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        ) : (
          <div className="ws-hero-reveal__placeholder" aria-hidden />
        )}
      </div>
      <div id="about" className="ws-hero-reveal__copy">
        <EditableContent
          fieldKey="eyebrow"
          editable={editable}
          activeEditTarget={target}
          className="ws-hero-reveal__eyebrow"
        >
          {eyebrow}
        </EditableContent>
        <EditableContent
          as={Heading}
          fieldKey="heading"
          editable={editable}
          activeEditTarget={target}
          className="ws-hero-reveal__title"
        >
          {heading}
        </EditableContent>
        {ctaPrimary ? (
          <EditableContent
            as="a"
            fieldKey="ctaPrimary"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-reveal__link"
            href={ctaPrimaryHref}
          >
            {ctaPrimary}
          </EditableContent>
        ) : null}
      </div>
    </section>
  );
}
